import React from 'react';

import { HIGH_PRECISION_DECIMAL_PLACES } from '@/constants';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { useLocalizedFormatter } from '@/hooks/useLocalizedFormatter';
import { LocaleContext } from '@/hooks/useTranslator';
import { CurrencyCodeType } from '@/types/currency';
import { Locale } from '@/types/locale';
import { NfTransactionDetailsResponse } from '@/types/simulation';
import { FeeType, FixedFee, PercentageFee } from '@/types/transaction';
import { formatAmount, FormatStyle } from '@/utility/formatAmount';

export interface TransactionDetailsTextValues {
  readonly grossAmountToConvert: string;
  readonly fixedFees: ReadonlyArray<FixedFee>;
  readonly percentageFeesWithoutTaxes: ReadonlyArray<PercentageFee>;
  readonly totalFeesValue: string;
  readonly totalAmountToConvert: string | null;
  readonly conversionRate: string | null;
  readonly marketExchangeRate: string | null;
  readonly calizaExchangeSpreadFeePercentage: string | null;
  readonly effectiveTransactionValue: string | null;
}

export const useTransactionDetails = (
  transactionDetails: NfTransactionDetailsResponse,
  fromAmount: number | null,
  fromCurrency: CurrencyCodeType,
): TransactionDetailsTextValues => {
  const rateFormatter = useLocalizedFormatter({
    maximumFractionDigits: HIGH_PRECISION_DECIMAL_PLACES,
    minimumFractionDigits: HIGH_PRECISION_DECIMAL_PLACES,
  });

  const percentageFormatter = useLocalizedFormatter({
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    style: 'percent',
  });

  const currencyFormatter = useCurrencyFormatter();
  const locale = React.useContext<Locale>(LocaleContext);

  return React.useMemo((): TransactionDetailsTextValues => {
    const { conversionDetails, feeDetails } = transactionDetails;
    const { totalFees, fixedFees, percentageFees } = feeDetails;
    const percentageFeesWithoutTaxes = percentageFees.filter(
      (fee: PercentageFee): boolean => fee.type !== FeeType.TAX,
    );

    const nonNullAmount = fromAmount ?? 0;
    const grossAmountToConvert = currencyFormatter.format(nonNullAmount, fromCurrency);
    const totalFeesCurrency = totalFees.currencyCode === '' ? fromCurrency : totalFees.currencyCode;
    const totalFeesValue = currencyFormatter.format(totalFees.value, totalFeesCurrency);

    const fixedFeesFormatted: ReadonlyArray<FixedFee> = fixedFees.map(
      (fee: FixedFee): FixedFee => ({
        ...fee,
        value: formatAmount(locale, Number(fee.value), fee.currencyCode, FormatStyle.extended),
      }),
    );

    const percentageFeesWithoutTaxesFormatted: ReadonlyArray<PercentageFee> =
      percentageFeesWithoutTaxes.map(
        (fee: PercentageFee): PercentageFee => ({
          ...fee,
          calculatedFee: formatAmount(
            locale,
            Number(fee.calculatedFee),
            fee.feeCurrency,
            FormatStyle.extended,
          ),
          percentageRate: percentageFormatter.format(Number(fee.percentageRate) / 100),
        }),
      );

    if (conversionDetails) {
      const {
        clientRate: clientExchangeRate,
        calizaRate: calizaExchangeSpreadFeePercentage,
        marketExchangeRate,
        amountToBeConverted,
        effectiveTransactionValue,
      } = conversionDetails;

      const totalAmountToConvert = amountToBeConverted
        ? formatAmount(locale, amountToBeConverted.value, amountToBeConverted.currencyCode)
        : '';

      const exchangeRate = clientExchangeRate ?? marketExchangeRate;

      const conversionRate = rateFormatter.format(exchangeRate);

      const calizaExchangeSpreadFeePercentageFormatted = percentageFormatter.format(
        calizaExchangeSpreadFeePercentage / 100,
      );

      const effectiveTransactionValueFormatted = rateFormatter.format(effectiveTransactionValue);

      const marketExchangeRateFormatted =
        marketExchangeRate && marketExchangeRate !== 0
          ? rateFormatter.format(marketExchangeRate)
          : null;

      return {
        grossAmountToConvert: grossAmountToConvert,
        fixedFees: fixedFeesFormatted,
        percentageFeesWithoutTaxes: percentageFeesWithoutTaxesFormatted,
        totalFeesValue: totalFeesValue,
        totalAmountToConvert: totalAmountToConvert,
        conversionRate: conversionRate,
        marketExchangeRate: marketExchangeRateFormatted,
        calizaExchangeSpreadFeePercentage: calizaExchangeSpreadFeePercentageFormatted,
        effectiveTransactionValue: effectiveTransactionValueFormatted,
      };
    } else {
      return {
        grossAmountToConvert: grossAmountToConvert,
        fixedFees: fixedFeesFormatted,
        percentageFeesWithoutTaxes: percentageFeesWithoutTaxesFormatted,
        totalFeesValue: totalFeesValue,
        totalAmountToConvert: null,
        conversionRate: null,
        marketExchangeRate: null,
        calizaExchangeSpreadFeePercentage: null,
        effectiveTransactionValue: null,
      };
    }
  }, [
    currencyFormatter,
    fromAmount,
    fromCurrency,
    locale,
    percentageFormatter,
    rateFormatter,
    transactionDetails,
  ]);
};
